import { useState } from 'react'

import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  Alert,
  Flex,
  HStack,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'
import {
  FaArrowUpFromBracketRegular,
  FaPlusRegular,
} from '@enechain/ecloud-icons'

import { PageScrollArea } from '~/components/common/PageScrollArea'
import { useErrorToast } from '~/hooks/useErrorToast'
import { type CivilDate, civilDateToProtoDate } from '~/models/civilDate'
import {
  AddPartnerNoticeDialog,
  type AddPartnerNoticeInput,
  type ExistingPartnerNotice,
} from '~/pages/SplitSupplyPlan/components/AddPartnerNoticeDialog'
import { ImportPartnerNoticesDialog } from '~/pages/SplitSupplyPlan/components/ImportPartnerNoticesDialog'
import {
  type PartnerImportResult,
  PartnerImportResultBanner,
} from '~/pages/SplitSupplyPlan/components/PartnerImportResultBanner'
import { PartnerNoticeTable } from '~/pages/SplitSupplyPlan/components/PartnerNoticeTable'
import { useCreatePartnerSplitSupplyNotice } from '~/pages/SplitSupplyPlan/hooks/useCreatePartnerSplitSupplyNotice'
import { useListPartnerSplitSupplyNotices } from '~/pages/SplitSupplyPlan/hooks/useListPartnerSplitSupplyNotices'
import {
  START_EDITING_STATE,
  partnerNoticeDetailSearch,
} from '~/pages/SplitSupplyPlan/models/partnerNotice'
import {
  SECTION_OF_TAB,
  type SectionTab,
} from '~/pages/SplitSupplyPlan/models/period'
import { describeSplitSupplyNoticeError } from '~/pages/SplitSupplyPlan/models/serverError'
import { routePaths } from '~/routes/routePaths'

type Props = {
  tab: SectionTab
  target: CivilDate
  area: AreaCode | undefined
  today: CivilDate
}

/**
 * 他社の通告値 (他社が通告値を出す契約の分)。 1 行 = 登録 1 件 (断面 × 対象日 × エリア × 他社 で 1 件だけ)。
 * 「追加」 で空の登録を作って詳細を編集モードで開き、 値は詳細で手で入れる。 同じ条件の登録が既にある時は
 * 作らず、 画面を移らずにダイアログの中で知らせて 「登録を開く」 リンクを置く。
 * 「取込」 で他社から受け取ったファイル (XML・ZIP・CSV) を選び、 確認画面で内容を見てから登録する
 * (`ImportPartnerNoticesDialog`)。
 * 行から詳細 (48 コマの確認・手直し・保存) へ進む。
 * 断面・対象日・エリアは上の絞り込み (`SectionFilterBar`) が決める。
 */
export const PartnerNoticesView = ({
  tab,
  target,
  area,
  today,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const showErrorToast = useErrorToast()
  const [isAddOpen, setAddOpen] = useState(false)
  const [isImportOpen, setImportOpen] = useState(false)
  // XML の取込でスキップした供給地点があった時の結果 (エラーバナーに出す)
  const [importResult, setImportResult] = useState<
    PartnerImportResult | undefined
  >(undefined)
  // 同じ条件の登録が既にあった時の、 その登録と追加した時の条件 (ダイアログに案内とリンクを出す)
  const [existing, setExisting] = useState<ExistingPartnerNotice | undefined>(
    undefined,
  )

  const { data, isLoading, isError, refetch } =
    useListPartnerSplitSupplyNotices({
      section: SECTION_OF_TAB[tab],
      targetDate: target,
      areaCode: area,
    })
  const { mutate: createNotice, isPending: isCreating } =
    useCreatePartnerSplitSupplyNotice()

  const handleAdd = (input: AddPartnerNoticeInput): void => {
    createNotice(
      {
        section: SECTION_OF_TAB[input.tab],
        targetDate: civilDateToProtoDate(input.targetDate),
        areaCode: input.areaCode,
        partnerBusinessMasterId: input.partnerBusinessMasterId,
      },
      {
        onSuccess: (res) => {
          if (!res.created) {
            // 画面は移らず、 ダイアログの中で知らせて 「登録を開く」 を置く
            setExisting({ id: res.id, input })
            return
          }
          setAddOpen(false)
          // 作った登録の詳細を編集モードで開き、 値を入れる (月間は対象月の 1 日から)
          void navigate(
            `${routePaths.planSplitSupplyDetail.path}?${partnerNoticeDetailSearch(res.id, input.targetDate)}`,
            { state: START_EDITING_STATE },
          )
        },
        onError: (err) => {
          showErrorToast({
            title: t('page:splitSupplyPlan.partner.addDialog.errorTitle'),
            description: describeSplitSupplyNoticeError(err, t),
          })
        },
      },
    )
  }

  return (
    <>
      <Flex alignItems="center" justifyContent="flex-end" gap={2}>
        <TertiaryButton
          onClick={() => {
            setImportOpen(true)
          }}
          size="md"
        >
          <HStack gap={1}>
            <FaArrowUpFromBracketRegular />
            <span>{t('page:splitSupplyPlan.partner.importButton')}</span>
          </HStack>
        </TertiaryButton>
        <TertiaryButton
          onClick={() => {
            setExisting(undefined)
            setAddOpen(true)
          }}
          size="md"
        >
          <HStack gap={1}>
            <FaPlusRegular />
            <span>{t('page:splitSupplyPlan.partner.addButton')}</span>
          </HStack>
        </TertiaryButton>
      </Flex>

      {importResult !== undefined && (
        <PartnerImportResultBanner
          onDismiss={() => {
            setImportResult(undefined)
          }}
          result={importResult}
        />
      )}

      {data === undefined && isError ? (
        <Alert
          description={
            <Flex alignItems="center" gap={3}>
              <span>{t('page:splitSupplyPlan.partner.fetchFailed')}</span>
              <TertiaryButton
                onClick={() => {
                  void refetch()
                }}
                size="sm"
              >
                {t('page:splitSupplyPlan.error.retry')}
              </TertiaryButton>
            </Flex>
          }
          status="error"
        />
      ) : (
        <PageScrollArea>
          <PartnerNoticeTable isLoading={isLoading} rows={data?.items ?? []} />
        </PageScrollArea>
      )}

      <AddPartnerNoticeDialog
        // 開くたびに断面・対象日・エリアの初期値 (一覧の現在の選択) から始める
        key={`${isAddOpen}-${tab}`}
        existing={existing}
        initialArea={area}
        initialTab={tab}
        initialTarget={target}
        isCreating={isCreating}
        isOpen={isAddOpen}
        onClose={() => {
          setAddOpen(false)
        }}
        onCreate={handleAdd}
        today={today}
      />
      <ImportPartnerNoticesDialog
        // 開くたびに選んだファイルと確認の状態を空から始める
        key={`import-${isImportOpen}-${tab}`}
        isOpen={isImportOpen}
        onClose={() => {
          setImportOpen(false)
        }}
        onSkipped={setImportResult}
        tab={tab}
      />
    </>
  )
}
